import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import {
    convertReceiptCurrency,
    suggestExchangeRate,
    type ExchangeRateSuggestion,
    type GroupDetail,
    type Receipt,
    type ReceiptCurrencyConversion,
} from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCurrencies } from '../../hooks/useCurrencies'
import { cekaNaVysledek } from '../../hooks/useReceipts'
import { formatDate, toDateInputValue } from '../../utils/format'
import { exponentOf, formatMoney, formatRate } from '../../utils/money'

/** Je účtenka rozpoznaná v měně, kterou skupina nepoužívá? */
export function jeCiziMenaUctenky(receipt: Receipt, group: GroupDetail): boolean {
    const mena = receipt.detectedCurrencyCode
    const hotovo = !cekaNaVysledek(receipt)
    return hotovo && !!mena && !group.currencyCodes.includes(mena)
}

/** Poznámka k útratě, aby bylo i později vidět, z čeho a jakým kurzem vznikla. */
export function poznamkaKPrepoctu(prepocet: ReceiptCurrencyConversion, exponentPuvodni: number): string {
    const puvodni = prepocet.originalTotal === null || prepocet.originalTotal === undefined ? '' : ` z ${formatMoney(prepocet.originalTotal, prepocet.from, exponentPuvodni)}`
    return `Přepočteno${puvodni} kurzem ${prepocet.source} ${formatRate(prepocet.rate)} ${prepocet.to}/${prepocet.from} ze dne ${formatDate(prepocet.day)}.`
}

export type ForeignCurrencyAlertProps = {
    receipt: Receipt
    group: GroupDetail
    onConverted: (prepocet: ReceiptCurrencyConversion) => void
}

/**
 * Upozornění na účtenku v měně, kterou skupina nevede.
 *
 * Takovou částku nejde uložit, proto se formulář z účtenky nepředvyplní a místo
 * toho se nabídne přepočet do hlavní měny skupiny dnešním kurzem ČNB.
 */
export function ForeignCurrencyAlert({ receipt, group, onConverted }: ForeignCurrencyAlertProps) {
    const { data: currencies } = useCurrencies()
    const mena = receipt.detectedCurrencyCode ?? ''
    const [kurz, setKurz] = useState<ExchangeRateSuggestion | null | undefined>()
    const [prepocitavam, setPrepocitavam] = useState(false)

    useEffect(() => {
        let zruseno = false
        void actionApiCall(
            () => suggestExchangeRate({ path: { groupId: group.id }, query: { currencyCode: mena, date: toDateInputValue(new Date()) } }),
            { silent: true },
        ).then((navrh) => {
            if (!zruseno) {
                setKurz(navrh ?? null)
            }
        })
        return () => {
            zruseno = true
        }
    }, [group.id, mena])

    const prepocitat = async () => {
        setPrepocitavam(true)
        try {
            const prepocet = await actionApiCall(() => convertReceiptCurrency({ path: { receiptId: receipt.id } }), {
                successMessage: `Účtenka přepočtena na ${group.baseCurrencyCode}.`,
            })
            if (prepocet) {
                onConverted(prepocet)
            }
        } finally {
            setPrepocitavam(false)
        }
    }

    return (
        <Alert variant="warning" className="py-2 small" data-testid="cizi-mena-uctenky">
            <div className="fw-semibold mb-1">
                <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                Účtenka je v měně {mena}, kterou tato skupina nepoužívá.
            </div>

            {receipt.detectedTotal !== null && receipt.detectedTotal !== undefined && (
                <div>Na účtence: {formatMoney(receipt.detectedTotal, mena, exponentOf(currencies, mena))}</div>
            )}

            {kurz === undefined ? (
                <div className="text-secondary">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Zjišťuji dnešní kurz…
                </div>
            ) : kurz === null ? (
                <div>Dnešní kurz {mena} není k dispozici — zadejte částku v {group.baseCurrencyCode} ručně.</div>
            ) : (
                <>
                    <div className="mb-2">
                        Dnešní kurz {kurz.source}: 1 {mena} = {formatRate(kurz.rate)} {group.baseCurrencyCode} (lístek z {formatDate(kurz.day)})
                    </div>
                    <Button variant="warning" size="sm" className="su-tappable" disabled={prepocitavam} onClick={() => void prepocitat()}>
                        {prepocitavam && <Spinner animation="border" size="sm" className="me-1" />}
                        Přepočítat na {group.baseCurrencyCode}
                    </Button>
                </>
            )}
        </Alert>
    )
}
